import { DownArrowIcon } from "@xola/icons";
import { Button, Currency, Label, Popover } from "@xola/ui-kit";
import { useFormikContext } from "formik";
import pluralize from "pluralize";
import React, { useState } from "react";
import { Purchase } from "types";
import { useSeller } from "../../../hooks/useAccount";
import { QuantitySpinner } from "../../Common/QuantitySpinner/QuantitySpinner";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { SplitPayFormValues } from "./CreatePurchaseSplitPayTypes";
import { getDivideAndPayAmount } from "./helpers";
import { SplitPayWarningAlert } from "./SplitPayComponents";

interface DivideAndPayPopoverContentProps {
    purchase: Purchase;
    divideParts: number;
    payParts: number;
    onCancel: () => void;
    onSubmit: (amount: number) => void;
}

const DivideAndPayPopoverContent = ({
    purchase,
    divideParts,
    payParts,
    onCancel,
    onSubmit,
}: DivideAndPayPopoverContentProps) => {
    const { amount, balance } = purchase;
    const { setFieldValue } = useFormikContext<SplitPayFormValues>();

    const handleDivideChange = (value: number) => {
        setFieldValue("divideParts", value);
        if (value < payParts) {
            setFieldValue("payParts", value);
        }
    };

    const handlePayPartsChange = (value: number) => {
        if (value > divideParts) {
            return;
        }

        setFieldValue("payParts", value);
    };

    return (
        <>
            <div className="w-full divide-y divide-gray-lighter">
                <DivideAndPayField
                    label="Divide Equally into"
                    amount={divideParts ? amount / divideParts : 0}
                    value={divideParts}
                    onChange={handleDivideChange}
                />
                <DivideAndPayField
                    label="Pay for"
                    amount={getDivideAndPayAmount(amount, divideParts, payParts, purchase)}
                    value={payParts}
                    onChange={handlePayPartsChange}
                />
            </div>
            {(amount / divideParts) * payParts > balance && (
                <SplitPayWarningAlert purchase={purchase} totalSum={(amount / divideParts) * payParts} />
            )}
            <div className="flex w-full flex-row-reverse space-x-2 space-x-reverse p-3">
                <Button type="button" disabled={payParts === 0} onClick={onSubmit}>
                    Apply
                </Button>
                <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
            </div>
        </>
    );
};

interface DivideAndPayFieldProps {
    label: string;
    amount: number;
    value: number;
    onChange: (value: number) => void;
}

const DivideAndPayField = ({ label, amount, value, onChange }: DivideAndPayFieldProps) => {
    const seller = useSeller();

    return (
        <div className="p-4">
            <Label>{label}</Label>
            <div className="flex items-center justify-center space-x-4 pt-1">
                <QuantitySpinner
                    value={value ?? 0}
                    text="parts"
                    min={0}
                    size="large"
                    classNames={{ label: "font-bold" }}
                    shouldSyncValue={false}
                    onChange={onChange}
                />
                <div className="flex-1">
                    <div>
                        <Currency currency={seller.currency}>{amount}</Currency>
                        {label === "Divide Equally into" ? "/part" : " Total"}
                    </div>
                </div>
            </div>
        </div>
    );
};

interface SplitPayDivideAndAddProps {
    purchase: Purchase;
    onSubmit: () => void;
    onClose: () => void;
    popoverParentId: string;
}

export const SplitPayDivideAndAdd = ({ purchase, onSubmit, onClose, popoverParentId }: SplitPayDivideAndAddProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const { values } = useFormikContext<SplitPayFormValues>();
    const { divideParts, payParts } = values;
    const content = document.querySelector(`#${popoverParentId}`);

    return (
        <Popover
            zIndex={50}
            visible={isVisible}
            maxWidth={350}
            appendTo={content}
            placement="bottom"
            trigger="click"
            onClickOutside={() => {
                setIsVisible(false);
            }}
        >
            <span
                onClick={() => {
                    setIsVisible(true);
                }}
            >
                <span> </span>
                <strong className="underline">
                    {divideParts > 0 && payParts > 0
                        ? `${divideParts} and pay for ${payParts} ${pluralize("part", payParts)}`
                        : "specific parts"}
                </strong>
                <DownArrowIcon className="flex-shrink-0" size="medium" />
            </span>
            <Popover.Content>
                <DivideAndPayPopoverContent
                    purchase={purchase}
                    divideParts={divideParts}
                    payParts={payParts}
                    onSubmit={() => {
                        setIsVisible(false);
                        onSubmit();
                    }}
                    onCancel={() => {
                        setIsVisible(false);
                        onClose();
                    }}
                />
            </Popover.Content>
        </Popover>
    );
};
